import React, { useCallback, useContext, useMemo, useState } from 'react';

import { cva } from 'class-variance-authority';
import { useLiveQuery } from 'dexie-react-hooks';
import Skeleton from 'react-loading-skeleton';

import db, { DEXIE_IS_LOADING } from '../../lib/db';
import { buildMessageEntitySnapshots } from '../../lib/entitySnapshots';
import { oxfordComma } from '../../lib/stringUtils';
import { cn } from '../../lib/utils';
import { isAvCallingEnabled } from '../../lib/workspace';

import AvatarIcon from '../../../assets/images/conference-transcript-avatar.svg';

import { EntitySnapshotContext } from '../../contexts/EntitySnapshotContext';
import { KEYCHAIN } from '../../contexts/KeyMaster';
import { useRouteWorkspace } from '../../contexts/RouteWorkspaceContext';
import ProfilePopoverContent from '../ProfilePopoverContent';
import DebugDumpModal from '../modals/DebugDumpModal';
import { ModalPortal, useRetainedModalValue } from '../modals/ModalPortal';
import AnimatedDots from '../shared/AnimatedDots';
import EntityPopover from '../shared/EntityPopover';
import LocalTime from './LocalTime';
import {
  AUTHOR_NAME_TYPOGRAPHY_CLASS,
  MESSAGE_CONTENT_TYPOGRAPHY_CLASS,
  MESSAGE_TIMESTAMP_TYPOGRAPHY_CLASS,
  SHORT_TIME_THRESHOLD,
} from './Message';
import RichdownContent from './RichdownContent';
import { getMessageBubbleSurfaceClassName } from './messageStyleClasses';

const formatDuration = (seconds) => {
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  }

  if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return `${hours}h ${minutes}m ${remainingSeconds}s`;
};

const actionButtonVariants = cva(
  'conference-action-button non-interactive-entities cursor-pointer rounded-full border px-3 py-1.5 font-sans text-xs font-blazeit',
  {
    variants: {
      variant: {
        default: 'border-gray-200 text-black dark:border-neutral-800 dark:text-neutral-200',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

const ActionButton = ({ variant, children, onClick }) => {
  return (
    <button data-app-region="none" className={cn(actionButtonVariants({ variant }))} onClick={onClick}>
      {children}
    </button>
  );
};

const StatusFooter = ({ conference_transcript, onReadMore }) => {
  const { ended_at, transcription_paused } = conference_transcript?.data ?? {};

  const wrapper = 'conference-transcript-status ml-15 pt-1 font-sans text-xs text-gray-500 dark:text-neutral-500';

  if (ended_at) {
    return (
      <div className={wrapper}>
        &#8627; Output is{' '}
        <span className="cursor-pointer text-blue-600 hover:text-blue-700" onClick={onReadMore}>
          summarizing
          <AnimatedDots />
        </span>
      </div>
    );
  } else if (transcription_paused) {
    return (
      <div className={wrapper}>
        &#8627; Output{' '}
        <span
          className="conference-status-paused cursor-pointer text-gray-800 italic hover:text-gray-900 dark:text-neutral-200 dark:hover:text-neutral-100"
          onClick={onReadMore}
        >
          paused transcription
        </span>
      </div>
    );
  } else {
    return (
      <div className={wrapper}>
        &#8627; Output is{' '}
        <span className="cursor-pointer text-blue-600 italic hover:text-blue-700" onClick={onReadMore}>
          transcribing
        </span>
      </div>
    );
  }
};

const ActionableButton = ({ actionable, onShowDebugDump }) => {
  const entitySnapshots = useContext(EntitySnapshotContext);
  const actionableTitleEntitySnapshots = useMemo(
    () => buildMessageEntitySnapshots(actionable.title, entitySnapshots),
    [actionable.title, entitySnapshots],
  );

  const handleOnClick = useCallback(() => {
    onShowDebugDump({ title: actionable.title, data: { kind: actionable.kind, prompt: actionable.prompt } });
  }, [actionable, onShowDebugDump]);

  return (
    <ActionButton onClick={handleOnClick}>
      <RichdownContent html={actionable.title} entitySnapshots={actionableTitleEntitySnapshots} richdownVariant="inline" />
    </ActionButton>
  );
};

const SummaryFooter = ({ conference_transcript, onReadMore, onShowDebugDump }) => {
  const { title, overview, actionables } = conference_transcript?.data ?? {};
  const entitySnapshots = useContext(EntitySnapshotContext);

  const titleEntitySnapshots = useMemo(() => buildMessageEntitySnapshots(title, entitySnapshots), [title, entitySnapshots]);
  const overviewEntitySnapshots = useMemo(() => buildMessageEntitySnapshots(overview, entitySnapshots), [overview, entitySnapshots]);

  return (
    <div className="ml-12 pt-1">
      <div className="conference-transcript-summary max-w-125 border-l border-gray-200 pl-3 font-sans text-cursed text-black dark:border-neutral-800 dark:text-neutral-200">
        {/* Title inherits the wrapper's color so the BG-image override reaches it too. */}
        <div className="font-semibold">
          <RichdownContent html={title} entitySnapshots={titleEntitySnapshots} richdownVariant="inline" />
        </div>

        <div className="pt-2">
          <RichdownContent html={overview} entitySnapshots={overviewEntitySnapshots} richdownVariant="inline" />
        </div>

        <div className="pt-3">
          <ActionButton onClick={onReadMore}>Read more</ActionButton>
        </div>
      </div>

      {actionables?.length > 0 && (
        <div className="flex flex-col items-start gap-1.5 pt-3">
          {actionables?.map((actionable) => (
            <ActionableButton key={actionable.id} actionable={actionable} onShowDebugDump={onShowDebugDump} />
          ))}
        </div>
      )}
    </div>
  );
};

export const ConferenceTranscript = ({ conference_transcript }) => {
  const { started_at, ended_at, actions, title } = conference_transcript?.data ?? {};

  // Modal state
  const [debugDumpData, setDebugDumpData] = useState(null);
  const retainedDebugDumpData = useRetainedModalValue(debugDumpData);

  // The transcript modal is owned by MessageFeed: this row is virtualized and can unmount
  // (scroll, jump, reconnect rebase) while the modal is open, which would tear down a
  // locally-rendered portal. The event carries the full row so the modal never re-fetches.
  const handleOpenTranscriptModal = useCallback(() => {
    if (!conference_transcript?.id) return;
    window.dispatchEvent(
      new CustomEvent('output:internal:messages:conference-transcript-modal', { detail: { message: conference_transcript } }),
    );
  }, [conference_transcript]);

  const handleShowDebugDump = useCallback((data) => setDebugDumpData(data), []);
  const handleCloseDebugDump = useCallback(() => setDebugDumpData(null), []);

  const participantProfileIds = useMemo(() => actions?.map((action) => action?.profile_id)?.filter(Boolean) ?? [], [actions]);
  const workspaceId = conference_transcript?.workspace_id;

  const participantProfiles = useLiveQuery(
    () =>
      participantProfileIds.length && workspaceId
        ? db.profiles
            .where('[workspace_id+id]')
            .anyOf(participantProfileIds.map((id) => [workspaceId, id]))
            .toArray()
        : [],
    [participantProfileIds.join('|'), workspaceId],
    DEXIE_IS_LOADING,
  );

  const profilesLoaded = participantProfiles !== DEXIE_IS_LOADING && participantProfiles.length > 0;

  const { profileId: currentProfileId } = useRouteWorkspace();
  const currentProfileIsParticipant = useMemo(
    () => participantProfileIds.includes(currentProfileId),
    [participantProfileIds, currentProfileId],
  );
  const channelId = conference_transcript?.channel_id;
  const channel = useLiveQuery(
    () => (workspaceId && channelId ? db.channels.get([workspaceId, channelId]) : null),
    [workspaceId, channelId],
    DEXIE_IS_LOADING,
  );
  const isSharedChannel = !!channel?.share_key;
  const currentWorkspace = useLiveQuery(() => (workspaceId ? db.workspaces.get(workspaceId) : null), [workspaceId], DEXIE_IS_LOADING);
  const canUseAvCalling = isAvCallingEnabled(currentWorkspace);

  // Clicking a participant shows the profile popover (via EntityPopover's clickEnabled) — never a
  // DM or account settings — so swallow the click before it reaches an interactive ancestor.
  const handleParticipantClick = useCallback((event) => {
    event?.stopPropagation();
  }, []);

  const duration = useMemo(() => {
    if (!started_at || !ended_at) return null;
    if (ended_at < started_at) return null;
    return formatDuration(ended_at - started_at);
  }, [started_at, ended_at]);

  const handleJoin = useCallback(() => {
    if (!conference_transcript?.channel_id) return;
    window.dispatchEvent(
      new CustomEvent('output:join-conference', {
        detail: { channelId: conference_transcript.channel_id, workspaceId, profileId: currentProfileId },
      }),
    );
  }, [conference_transcript?.channel_id, workspaceId, currentProfileId]);

  return (
    <div data-debug-id={conference_transcript.id} data-debug-sort-order={conference_transcript.sort_order} className="flex flex-col">
      <div className="flex w-full flex-col gap-1 pt-6">
        {/* Header, avatar, and bubble mirror the user message row anatomy from
            Message.jsx/MessageAvatar.jsx: same gutter (ml-12), header classes, 38px
            avatar pinned to the bubble's bottom, and hairline avatar ring. */}
        <div className="relative py-px leading-0">
          <div className="ml-12 flex items-center pb-1">
            <div className="flex min-w-0 items-center gap-[6px] text-cursed md:text-3xs">
              <span className={cn('inline-block truncate select-none', AUTHOR_NAME_TYPOGRAPHY_CLASS)}>AV Call</span>
              <LocalTime
                timestamp={conference_transcript.timestamp}
                format={Date.now() / 1000 - conference_transcript.timestamp > SHORT_TIME_THRESHOLD ? '%-m/%-d/%y · %-I:%M %p' : '%-I:%M %p'}
                className={cn('select-none', MESSAGE_TIMESTAMP_TYPOGRAPHY_CLASS)}
              />
              {started_at && !ended_at && !currentProfileIsParticipant && !isSharedChannel && canUseAvCalling && (
                <button
                  onClick={handleJoin}
                  className="cursor-pointer font-sans text-[11px]/[20px] text-blue-600 select-none hover:text-blue-700"
                >
                  Join
                </button>
              )}
            </div>
          </div>

          <div className="absolute bottom-px left-0 size-[38px]">
            <div className="conference-transcript-avatar flex size-[38px] items-center justify-center rounded-full">
              <AvatarIcon className="text-white" />
            </div>
            <div className="pointer-events-none absolute inset-0 rounded-full border-[0.5px] border-black/10 dark:hidden" />
          </div>

          <div className="relative ml-12 flex">
            {!profilesLoaded && <Skeleton count={1} width={100} height={35.5} />}
            {/* Exactly the not-mine message bubble: same surface, shape, and typography
                classes as Message.jsx, so it inherits the BG-image glass rules too. The
                inner div restores inline flow inside the bubble's flex-col. */}
            {profilesLoaded && (
              <div className={cn('richdown-bubble--base max-w-[520px]', getMessageBubbleSurfaceClassName(false))}>
                <div className={cn('richdown-content--base richdown-content--channel-message-not-mine', MESSAGE_CONTENT_TYPOGRAPHY_CLASS)}>
                  <div className="inline-block">with&nbsp;</div>

                  {participantProfiles.map((profile, index) => {
                    const isDeactivated = profile?.role === 'deactivated';
                    return (
                      <React.Fragment key={profile.id}>
                        <EntityPopover content={<ProfilePopoverContent profile={profile} />} clickEnabled={true}>
                          <button
                            data-app-region="none"
                            data-profile-id={profile.id}
                            onClick={handleParticipantClick}
                            className={cn(
                              'inline-block max-w-100 truncate font-semibold',
                              isDeactivated ? 'cursor-default text-gray-400 italic' : 'cursor-pointer',
                            )}
                          >
                            {profile?.full_name || 'Unknown'}
                          </button>
                        </EntityPopover>
                        {oxfordComma(index, participantProfiles.length)}
                      </React.Fragment>
                    );
                  })}

                  {duration && <div className="inline-block">&nbsp;for {duration}</div>}
                </div>
              </div>
            )}
          </div>
        </div>

        {!title && <StatusFooter conference_transcript={conference_transcript} onReadMore={handleOpenTranscriptModal} />}
        {title && (
          <SummaryFooter
            conference_transcript={conference_transcript}
            onReadMore={handleOpenTranscriptModal}
            onShowDebugDump={handleShowDebugDump}
          />
        )}
      </div>

      <ModalPortal open={Boolean(debugDumpData)} onClose={handleCloseDebugDump} size="mini" keychainId={KEYCHAIN.MODAL__DEBUG_DUMP}>
        {retainedDebugDumpData ? (
          <DebugDumpModal title={retainedDebugDumpData.title} data={retainedDebugDumpData.data} onClose={handleCloseDebugDump} />
        ) : null}
      </ModalPortal>
    </div>
  );
};

export default ConferenceTranscript;
